import { useEffect, useMemo, useState } from "react";
import {
  Building2, Check, Clipboard, Hash, Loader2, Lock,
  Mail, Phone, Save, ShieldCheck, Upload, UserRound, X,
} from "lucide-react";
import { toast } from "sonner";

import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDate, initials } from "@/lib/utils-ros";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin: "Admin",
  founder: "Founder / CRO",
  growth_marketing: "Growth Marketing",
  ergoniq_sales: "Ergoniq Sales",
  partner: "Partner",
};

export default function Settings() {
  const { user, role, refreshMe } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", avatar_url: "" });
  const [partner, setPartner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [passwordBusy, setPasswordBusy] = useState(false);

  useEffect(() => {
    setForm({
      name: user?.name || "",
      phone: user?.phone || "",
      avatar_url: user?.avatar_url || "",
    });
  }, [user?.name, user?.phone, user?.avatar_url]);

  useEffect(() => {
    if (!user?.partner_id) { setPartner(null); return; }
    api.get(`/partner-firms/${user.partner_id}`)
      .then(response => setPartner(response.data))
      .catch(() => setPartner(null));
  }, [user?.partner_id]);

  const dirty = useMemo(() => (
    form.name !== (user?.name || "") ||
    form.phone !== (user?.phone || "") ||
    form.avatar_url !== (user?.avatar_url || "")
  ), [form, user]);

  const allowedModules = useMemo(() => Object.entries(user?.permissions || {})
    .filter(([, allowed]) => allowed)
    .map(([module]) => module.replaceAll("_", " ")), [user?.permissions]);

  async function choosePhoto(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image is too large (maximum 8 MB)"); return; }
    setPhotoBusy(true);
    try {
      const avatar_url = await resizeImageToDataUrl(file, 320);
      setForm(current => ({ ...current, avatar_url }));
    } catch {
      toast.error("Could not read that image");
    } finally { setPhotoBusy(false); }
  }

  function resetForm() {
    setForm({ name: user?.name || "", phone: user?.phone || "", avatar_url: user?.avatar_url || "" });
  }

  async function saveProfile(event) {
    event.preventDefault();
    const name = form.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await api.patch("/auth/me", { name, phone: form.phone.trim(), avatar_url: form.avatar_url });
      await refreshMe();
      toast.success("Profile updated");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update profile");
    } finally { setSaving(false); }
  }

  async function changePassword(event) {
    event.preventDefault();
    if (passwordForm.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (!/[A-Za-z]/.test(passwordForm.new_password) || !/\d/.test(passwordForm.new_password)) { toast.error("New password must include a letter and a number"); return; }
    if (passwordForm.new_password !== passwordForm.confirm_password) { toast.error("New passwords do not match"); return; }
    setPasswordBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      localStorage.setItem("ros_token", data.token);
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      toast.success("Password changed · other sessions signed out");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not change password");
    } finally { setPasswordBusy(false); }
  }

  return (
    <div className="min-h-full bg-[#F7FAFC]" data-testid="settings-page">
      <div className="bg-[linear-gradient(to_bottom,#E6F5FB_0%,#FFFFFF_100%)] border-b border-[#DCE8EE] px-4 md:px-8 py-7 md:py-9">
        <div className="max-w-6xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--copper)] mb-1">My account</div>
          <h1 className="font-display text-3xl tracking-tight text-[#07539A]">User settings</h1>
          <p className="mt-1 text-[12px] text-[#607D8B]">Manage your personal profile and view your Ergoniq workspace identity.</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)] gap-5">
        <form onSubmit={saveProfile} className="rounded-2xl border border-[#D2E2E9] bg-white shadow-[0_10px_30px_rgba(7,83,154,0.07)] overflow-hidden" data-testid="profile-form">
          <div className="px-5 md:px-6 py-4 border-b border-[#E1EBEF] flex items-center gap-2">
            <UserRound size={16} className="text-[#07539A]" />
            <h2 className="font-display text-lg text-[#102A43]">Personal profile</h2>
          </div>

          <div className="p-5 md:p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5 rounded-xl bg-[#F4FAFD] border border-[#D8E9F0] p-4">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-[#07539A] text-[#FFFFFF] flex items-center justify-center text-2xl font-display shadow-[0_8px_22px_rgba(7,83,154,0.2)] shrink-0">
                {form.avatar_url
                  ? <img src={form.avatar_url} alt="Profile preview" className="w-full h-full object-cover" />
                  : initials(form.name || user?.name || "")}
              </div>
              <div className="min-w-0">
                <div className="font-display text-lg text-[#102A43]">Profile photo</div>
                <p className="text-[10px] text-[#78909C] mt-1">JPG, PNG or WebP up to 8 MB. Images are resized securely before saving.</p>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <label className="h-9 px-3 rounded-lg bg-[#07539A] text-[#FFFFFF] text-[11px] font-medium flex items-center gap-1.5 cursor-pointer hover:bg-[#063F73]">
                    {photoBusy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                    {form.avatar_url ? "Change photo" : "Upload photo"}
                    <input type="file" accept="image/*" onChange={choosePhoto} className="hidden" data-testid="settings-avatar-input" />
                  </label>
                  {form.avatar_url && (
                    <button type="button" onClick={() => setForm(current => ({ ...current, avatar_url: "" }))} data-testid="settings-avatar-remove"
                      className="h-9 px-3 rounded-lg border border-[#CADBE3] text-[#607D8B] hover:text-[#B42318] hover:border-[#F0B9B4] text-[11px] flex items-center gap-1.5">
                      <X size={13} /> Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <ProfileField icon={UserRound} label="Display name" value={form.name} onChange={name => setForm(current => ({ ...current, name }))} testId="settings-name" />
              <ProfileField icon={Phone} label="Phone" value={form.phone} onChange={phone => setForm(current => ({ ...current, phone }))} placeholder="Add a phone number" testId="settings-phone" />
              <ReadOnlyField icon={Mail} label="Email" value={user?.email || "—"} note="Contact an admin to change your sign-in email." />
              <ReadOnlyField icon={ShieldCheck} label="Role" value={ROLE_LABELS[role] || String(role || "—").replaceAll("_", " ")} note="Roles and permissions are admin-controlled." />
            </div>
          </div>

          <div className="px-5 md:px-6 py-4 border-t border-[#E1EBEF] bg-[#FAFCFD] flex items-center justify-end gap-2">
            <button type="button" onClick={resetForm} disabled={!dirty || saving} data-testid="settings-discard"
              className="h-10 px-4 rounded-lg border border-[#CADBE3] text-[#607D8B] text-[12px] disabled:opacity-40 hover:border-[#91B6C8]">Discard</button>
            <button type="submit" disabled={!dirty || saving || photoBusy} data-testid="settings-save"
              className="h-10 px-5 rounded-lg bg-[#07539A] text-[#FFFFFF] text-[12px] font-medium shadow-[0_6px_14px_rgba(7,83,154,0.18)] disabled:opacity-45 flex items-center gap-2 hover:bg-[#063F73]">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save changes
            </button>
          </div>
        </form>

        <div className="space-y-5">
          <section className="rounded-2xl border border-[#D2E2E9] bg-white shadow-[0_10px_30px_rgba(7,83,154,0.07)] overflow-hidden" data-testid="ergoniq-identity">
            <div className="px-5 py-4 border-b border-[#E1EBEF] flex items-center gap-2">
              <Hash size={15} className="text-[#07539A]" />
              <h2 className="font-display text-lg text-[#102A43]">Ergoniq identity</h2>
            </div>
            <div className="p-5 space-y-3">
              <CopyField label="Ergoniq user ID" value={user?.id} testId="copy-user-id" />
              <CopyField label="Partner ID" value={user?.partner_id} testId="copy-partner-id" empty="Not linked to a partner" />
              <CopyField label="Manager ID" value={user?.manager_id} testId="copy-manager-id" empty="No manager assigned" />
              {partner && (
                <div className="rounded-xl bg-[#F4FAFD] border border-[#D8E9F0] p-3 flex items-center gap-3">
                  <span className="w-9 h-9 rounded-lg bg-[#E1F2F8] text-[#07539A] flex items-center justify-center"><Building2 size={15} /></span>
                  <span className="min-w-0">
                    <span className="block text-[11px] text-[#78909C]">Partner organisation</span>
                    <span className="block text-[13px] font-medium text-[#102A43] truncate">{partner.firm || partner.name}</span>
                  </span>
                </div>
              )}
            </div>
          </section>

          <section className="rounded-2xl border border-[#D2E2E9] bg-white shadow-[0_10px_30px_rgba(7,83,154,0.07)] p-5" data-testid="account-details">
            <div className="flex items-center gap-2 mb-4"><Lock size={15} className="text-[#07539A]" /><h2 className="font-display text-lg text-[#102A43]">Account details</h2></div>
            <dl className="space-y-3 text-[11px]">
              <DetailRow label="Account created" value={formatDate(user?.created_at)} />
              <DetailRow label="Last sign-in" value={formatDate(user?.last_login_at)} />
              <DetailRow label="Profile updated" value={formatDate(user?.updated_at)} />
            </dl>
            <div className="mt-5 pt-4 border-t border-[#E1EBEF]">
              <div className="text-[10px] uppercase tracking-wider text-[#78909C] mb-2">Accessible modules</div>
              <div className="flex flex-wrap gap-1.5">
                {allowedModules.map(module => <span key={module} className="px-2 py-1 rounded-full bg-[#E6F4FA] text-[#07539A] text-[9px] capitalize">{module}</span>)}
              </div>
            </div>
          </section>

          <form onSubmit={changePassword} className="rounded-2xl border border-[#D2E2E9] bg-white shadow-[0_10px_30px_rgba(7,83,154,0.07)] overflow-hidden" data-testid="change-password-form">
            <div className="px-5 py-4 border-b border-[#E1EBEF] flex items-center gap-2">
              <Lock size={15} className="text-[#07539A]" />
              <h2 className="font-display text-lg text-[#102A43]">Change password</h2>
            </div>
            <div className="p-5 space-y-3">
              <PasswordField label="Current password" value={passwordForm.current_password} onChange={current_password => setPasswordForm(current => ({ ...current, current_password }))} testId="current-password" />
              <PasswordField label="New password" value={passwordForm.new_password} onChange={new_password => setPasswordForm(current => ({ ...current, new_password }))} testId="new-password" />
              <PasswordField label="Confirm new password" value={passwordForm.confirm_password} onChange={confirm_password => setPasswordForm(current => ({ ...current, confirm_password }))} testId="confirm-password" />
              <p className="text-[10px] text-[#78909C]">Use at least 8 characters with a letter and number. Changing it signs out your other sessions.</p>
              <button type="submit" disabled={passwordBusy || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password} data-testid="change-password-submit"
                className="w-full h-10 rounded-xl bg-[#07539A] hover:bg-[#063F73] text-white text-[12px] font-medium disabled:opacity-40 flex items-center justify-center gap-2">
                {passwordBusy ? <Loader2 size={13} className="animate-spin" /> : <Lock size={13} />} Update password
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] font-medium text-[#526D7A] mb-1.5 block">{label}</span>
      <input type="password" autoComplete="new-password" value={value} onChange={event => onChange(event.target.value)} data-testid={testId}
        className="w-full h-10 px-3 rounded-xl border border-[#C8DAE3] bg-white outline-none focus:border-[#00A2D4] focus:ring-2 focus:ring-[#00A2D4]/10 text-[13px]" />
    </label>
  );
}

function ProfileField({ icon: Icon, label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] font-medium text-[#526D7A] flex items-center gap-1.5 mb-1.5"><Icon size={12} className="text-[#07539A]" /> {label}</span>
      <input value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} data-testid={testId}
        className="w-full h-11 px-3 rounded-xl border border-[#C8DAE3] bg-white outline-none focus:border-[#00A2D4] focus:ring-2 focus:ring-[#00A2D4]/10 text-[13px]" />
    </label>
  );
}

function ReadOnlyField({ icon: Icon, label, value, note }) {
  return (
    <div>
      <div className="text-[10px] font-medium text-[#526D7A] flex items-center gap-1.5 mb-1.5"><Icon size={12} className="text-[#07539A]" /> {label}</div>
      <div className="min-h-11 px-3 py-2 rounded-xl border border-[#D8E3E8] bg-[#F5F8F9] text-[13px] text-[#526D7A] flex items-center">{value}</div>
      <div className="text-[9px] text-[#91A3AC] mt-1">{note}</div>
    </div>
  );
}

function CopyField({ label, value, empty = "Not available", testId }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }
  return (
    <div className="rounded-xl border border-[#DCE7EC] bg-[#FBFDFE] px-3 py-2.5 flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <div className="text-[9px] uppercase tracking-wider text-[#78909C]">{label}</div>
        <div className={`mt-0.5 text-[10px] font-mono truncate ${value ? "text-[#102A43]" : "text-[#A1B2BA]"}`}>{value || empty}</div>
      </div>
      {value && (
        <button type="button" onClick={copy} data-testid={testId} aria-label={`Copy ${label}`}
          className="w-8 h-8 rounded-lg border border-[#D0E0E7] text-[#07539A] hover:bg-[#E7F5FB] flex items-center justify-center shrink-0">
          {copied ? <Check size={13} /> : <Clipboard size={13} />}
        </button>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  return <div className="flex items-center justify-between gap-3"><dt className="text-[#78909C]">{label}</dt><dd className="font-mono text-[10px] text-[#102A43] text-right">{value}</dd></div>;
}

function resizeImageToDataUrl(file, maxSize) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error("decode failed"));
      image.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(image, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.86));
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
